import { useEffect, useRef } from 'react'
import mapboxgl from 'mapbox-gl'
import { useLocation } from 'react-router-dom'
import { detectCityFromURL } from '../utils/cityDetector'
import { saveViewport } from '../utils/mapViewport'

// Terser mangles the inline worker blob in production; use the pre-built worker instead
mapboxgl.workerUrl = process.env.PUBLIC_URL + '/mapbox-gl-csp-worker.js'
mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN

export const useInitializeMap = ({
  mapRef,
  mapContainer,
  setMapLoaded,
  styleUrl,
  center,
  zoom,
}) => {
  const location = useLocation()
  // Ref para mantener el pathname actualizado sin recrear el mapa
  const pathnameRef = useRef(location.pathname)

  // Actualizar la ref cuando cambia el pathname
  useEffect(() => {
    pathnameRef.current = location.pathname
  }, [location.pathname])

  useEffect(() => {
    if (!mapContainer) return

    const map = new mapboxgl.Map({
      container: mapContainer,
      style: styleUrl,
      center,
      zoom,
      pitch: 30,
      maxPitch: 80,
    })

    map.addControl(new mapboxgl.NavigationControl(), 'bottom-right')

    const geolocate = new mapboxgl.GeolocateControl({
      positionOptions: { enableHighAccuracy: false },
      showAccuracyCircle: true,
      fitBoundsOptions: { maxZoom: 17 },
      showUserHeading: false,
      showUserLocation: true,
      trackUserLocation: true,
    })

    map.addControl(geolocate)

    // Usar 'moveend' es más eficiente que 'move' (se dispara al terminar de mover).
    // El viewport se guarda en sessionStorage para recordarlo durante la sesión.
    const handleMoveEnd = () => {
      const mapCenter = map.getCenter()
      saveViewport({
        city: detectCityFromURL(pathnameRef.current),
        center: [mapCenter.lng, mapCenter.lat],
        zoom: map.getZoom(),
      })
    }

    map.on('moveend', handleMoveEnd)

    mapRef.current = map

    map.on('load', () => setMapLoaded(true))

    // Cleanup: destruir el mapa cuando el contenedor cambia o el componente se desmonta.
    // Esto cubre el caso de React 18 donde el estado null intermedio se omite por batching
    // (ej: mainDOM → wizardDOM sin pasar por null).
    return () => {
      map.off('moveend', handleMoveEnd)
      if (mapRef.current === map) {
        mapRef.current = null
      }
      setMapLoaded(false)
      // Diferir remove() al siguiente frame: si hay un animation frame de Mapbox ya encolado,
      // permitir que termine antes de destruir el estado interno (evita crash en .projection).
      const mapToRemove = map
      requestAnimationFrame(() => {
        try {
          mapToRemove.remove()
        } catch (e) {
          // Ignorar errores si el contenedor ya fue removido del DOM
        }
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapContainer])
}
